import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Globe,
  AlertCircle
} from 'lucide-react';
import { Button, PillToggle } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import CreateCountryModal from '../../components/admin/CreateCountryModal';
import { useToastContext } from "../../contexts/ToastContext";

import { 
  useAdminCountries, 
  useCreateCountry, 
  useUpdateCountry, 
  useDeleteCountry, 
  useToggleCountryStatus
} from '../../services/countryService';

const AdminCountries = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const { success, error: showError } = useToastContext();

  // API query parameters - no pagination since DataTable handles it
  const queryParams = {
    sort_by: 'sort_order',
    sort_order: 'asc'
  };

  // Fetch countries from API
  const { 
    data: countriesData, 
    isLoading: loading, 
    error: countriesError,
    refetch 
  } = useAdminCountries(queryParams);
//   console.log('====================================');
//   console.log(countriesData);
//   console.log('====================================');
  // Extract countries from API response
  const countries = countriesData?.data || [];

  // API mutations
  const createCountryMutation = useCreateCountry({
    onSuccess: () => {
      success('Country created successfully', 'success');
      setShowCreateModal(false);
    },
    onError: (error) => {
      showError(error.message || 'Failed to create country', 'error');
    }
  });

  const updateCountryMutation = useUpdateCountry({
    onSuccess: () => {
      success('Country updated successfully', 'success');
      setShowEditModal(false);
      setSelectedCountry(null);
    },
    onError: (error) => {
      showError(error.message || 'Failed to update country', 'error');
    }
  });

  const deleteCountryMutation = useDeleteCountry({
    onSuccess: () => {
      success('Country deleted successfully', 'success');
    },
    onError: (error) => {
      showError(error.message || 'Failed to delete country', 'error');
    }
  });

  const toggleStatusMutation = useToggleCountryStatus({
    onSuccess: () => {
      success('Country status updated successfully');
    },
    onError: (error) => {
      showError(error.message || 'Failed to update country status');
    }
  });

  const isStatusToggling = (countryId) =>
    toggleStatusMutation.isPending && toggleStatusMutation.variables === countryId;

  const handleToggleStatus = (countryId) => {
    toggleStatusMutation.mutate(countryId);
  };

  const handleDeleteCountry = (countryId) => {
    if (window.confirm('Are you sure you want to delete this country?')) {
      deleteCountryMutation.mutate(countryId);
    }
  };

  const openEditModal = (country) => {
    setSelectedCountry(country);
    setShowEditModal(true);
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'name',
      label: 'Country',
      sortable: true,
      searchable: true,
      render: (country) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
              {country.emoji ? (
                <span className="text-lg">{country.emoji}</span>
              ) : (
                <Globe className="h-5 w-5 text-white" />
              )}
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {country.name}
            </div>
            {country.unicode && (
              <div className="text-sm text-gray-500">
                Unicode: {country.unicode}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      searchable: true,
      render: (country) => (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {country.code}
        </span>
      )
    },
    {
      key: 'dial_code',
      label: 'Dial Code',
      sortable: true,
      searchable: true,
      render: (country) => country.dial_code || '-'
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      render: (country) => (
        <PillToggle
          checked={Boolean(country.is_active)}
          onChange={() => handleToggleStatus(country.id)}
          loading={isStatusToggling(country.id)}
          variant="success"
          onLabel="On"
          offLabel="Off"
          ariaLabel={`${country.name} active status`}
        />
      ),
    },
    {
      key: 'sort_order',
      label: 'Sort Order',
      sortable: true,
      render: (country) => country.sort_order
    }
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: (country) => openEditModal(country),
      title: 'Edit Country',
      variant: 'default',
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (country) => handleDeleteCountry(country.id),
      title: 'Delete Country',
      variant: 'danger',
    },
  ];

  const handleCreateCountry = (countryData) => {
    createCountryMutation.mutate(countryData);
  };

  const handleEditCountry = (countryData) => {
    updateCountryMutation.mutate({
      id: selectedCountry.id,
      data: countryData
    });
  };

  // Handle API errors
  if (countriesError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Error Loading Countries</h3>
          <p className="mt-1 text-sm text-gray-500">
            {countriesError.message || 'Failed to load countries'}
          </p>
          <div className="mt-6">
            <Button onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading countries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Countries</h1>
            <p className="mt-2 text-gray-600">Manage countries and their information</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Country
            </Button>
          </div>
        </div>

        {/* Countries DataTable */}
        <DataTable
          data={countries}
          columns={columns}
          actions={actions}
          loading={loading}
          searchable={true}
          filterable={false}
          sortable={true}
          selectable={false}
          pageSize={20}
          showPagination={true}
          searchPlaceholder="Search countries..."
          emptyMessage="No countries found. Get started by creating a new country."
          className="mb-6"
        />

        {/* Create Modal */}
        {showCreateModal && (
          <CreateCountryModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreateCountry}
            isLoading={createCountryMutation.isLoading}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && selectedCountry && (
          <CreateCountryModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedCountry(null);
            }}
            onSave={handleEditCountry}
            country={selectedCountry}
            isEdit={true}
            isLoading={updateCountryMutation.isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default AdminCountries;
